// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartPropsFormSchema.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

!(function() {
  var AppGlobal = require('Izenda.AppGlobal');
  var SCHEMA_DEF = require('logic/data/Izenda.ReportPartPropsFormSchema');
  var TinyMCEUtils = require('share/components/controls/TinyMCE/Izenda.TinyMCEUtils');
  var constants = require('logic/data/Izenda.ReportPartFormConstants');
  var ReportDesignerConstants = require('logic/data/Izenda.ReportDesignerConstants');
  var Cancellation = require('share/errorHandling/Izenda.Cancellation');
  var m = require('mobx');
  const { isEqual, get, assign, isArray, getOr } = require('lodash/fp');
  var WIDGETS = {
    Panel: require('share/components/controls/Izenda.Panel'),
    FontStyle: require('share/components/controls/Izenda.FontStyle'),
    FontFormat: require('share/components/controls/Izenda.FontFormat'),
    ColorStyling: require('share/components/controls/Izenda.ColorStyling'),
    ButtonGroup: require('share/components/controls/Izenda.ButtonGroup')
  };

  module.exports = function(reportDesignerModel, reportPart, fieldStore, itemProps) {
    ensure(!!reportDesignerModel, 'reportDesignerModel must be set');

    var reportPartContent = reportPart.reportPartContent;

    var isHTMLTabActive = reportPart.isBackSide && reportPartContent.activeTabKey === constants.TAB_HTML.key;

    var getProperties = function(key, defaultValue) {
      if (!key) {
        var properties = reportPart && reportPart.getReportPartProperties();
        var props = m.toJS(properties);
        return props;
      }
      return reportPartContent.getReportPartProperties(key, defaultValue);
    };

    return {
      // define data to render UI
      definedSchema: SCHEMA_DEF,

      // list of widgets which are used to render UI
      widgets: WIDGETS,

      // data to binding to fields
      source: {},

      // properties for different fields
      mappingProps: {
        beforeAddField: function() {
          var fieldLimitValue = AppGlobal.advancedSettings.performance.fieldLimitValue;
          if (reportPart.fieldCollection.length >= fieldLimitValue) {
            return itemProps
              .confirmMessage('error', ReportDesignerConstants.BUNDLE.MESSAGE.FIELDS_EXCEEDED_LIMITATION)
              .finally(function() {
                return Promise.reject(new Cancellation());
              });
          }

          return Promise.resolve(arguments);
        },

        //Restriction for preidcted field to be added here as well if required
        fnResetBorderDefaultValue: reportPartContent.resetBorderDefaultValue.bind(reportPartContent),
        fnResetRefreshIntervalDefaultValue: reportPartContent.resetRefreshIntervalValue.bind(reportPartContent),
        getBorderSettings: function() {
          return {
            isVisibleBorder: getProperties('isVisibleBorder', false),
            borderStyle: getProperties('borderStyle', constants.FORM_STYLE_DEFAULT.borderStyle),
            borderColor: getProperties('borderColor', constants.FORM_STYLE_DEFAULT.borderColor),
            borderWidth: getProperties('borderWidth', constants.FORM_STYLE_DEFAULT.borderWidth)
          };
        },
        hadSettingBorder: function() {
          var borderSetting = {
            isVisibleBorder: getProperties('isVisibleBorder', false),
            borderStyle: getProperties('borderStyle', constants.FORM_STYLE_DEFAULT.borderStyle),
            borderColor: getProperties('borderColor', constants.FORM_STYLE_DEFAULT.borderColor),
            borderWidth: getProperties('borderWidth', constants.FORM_STYLE_DEFAULT.borderWidth)
          };
          var defaultSetting = {
            isVisibleBorder: false,
            borderStyle: constants.FORM_STYLE_DEFAULT.borderStyle,
            borderColor: constants.FORM_STYLE_DEFAULT.borderColor,
            borderWidth: constants.FORM_STYLE_DEFAULT.borderWidth
          };

          return !isEqual(borderSetting, defaultSetting);
        },

        getDataRefreshInterval: function() {
          return getProperties('dataRefreshInterval', {});
        },

        hadRefreshInterval: function() {
          var defaultSetting = constants.DEFAULT_SETTING_VALUE.refreshIntervalDefault;
          var refreshSetting = getProperties('dataRefreshInterval', {});

          return (
            refreshSetting.value !== defaultSetting.value ||
            refreshSetting.updateInterval !== defaultSetting.updateInterval
          );
        },

        getFieldQuerySource: function() {
          return {
            fieldQuerySource: fieldStore.fieldQuerySource
          };
        }
      },

      // defined-function which fields need to execute to get its data
      mappingSource: {
        getBackgroundColor: function() {
          return getProperties('backgroundColor', constants.FORM_STYLE_DEFAULT.backgroundColor);
        },

        hadSettingBackgroundColor: function() {
          var backgroundColor = getProperties('backgroundColor', constants.FORM_STYLE_DEFAULT.backgroundColor);

          return backgroundColor !== constants.FORM_STYLE_DEFAULT.backgroundColor;
        },

        getToolbarSetting: function() {
          return {
            pasteAsText: getProperties('pasteAsText', false)
          };
        },

        getHightlightCodeValue: function() {
          return getProperties('highlightCode', true);
        },

        getWrapTextValue: function() {
          return getProperties('wrapText', true);
        },

        getEditorControl: function() {
          return reportPartContent.getEditorControl();
        },

        getEditButtonHidden: function() {
          var hidden = !reportPartContent.isActiveForEdit || isHTMLTabActive;
          return {
            cut: hidden,
            copy: hidden,
            paste: hidden,
            pasteAsText: hidden,
            selectAll: isHTMLTabActive,
            searchReplace: isHTMLTabActive,
            searchReplaceHTML: !isHTMLTabActive
          };
        },

        getDirectionSetting: function() {
          return {
            mceDirectionRTL: getProperties('mceDirectionRTL', false),
            mceDirectionLTR: getProperties('mceDirectionLTR', true)
          };
        },

        getShowInvisibleCharacters: function() {
          return getProperties('mceVisualChars', false);
        },

        getVisualAids: function() {
          return getProperties('mceToggleVisualAid', true);
        },

        getUsePagination: function() {
          return getProperties('usePagination', false);
        },

        getPageBreakAfterEachEntry: function() {
          return getProperties('pageBreakAfterEachEntry', false);
        },

        isHiddenTableCell: function() {
          return !reportPartContent.isSelectedCell;
        },

        isActiveForEdit: function() {
          return !reportPartContent.isActiveForEdit || isHTMLTabActive;
        },

        isViewFrontSide: function() {
          return !reportPart.isBackSide;
        },

        isHiddenHTMLCode: function() {
          return !reportPart.isBackSide || reportPartContent.activeTabKey !== constants.TAB_HTML.key;
        },

        getTableButtonHidden: function() {
          var hidden = !reportPartContent.isSelectedCell;
          return {
            tableDelete: hidden,
            tableProperties: hidden
          };
        },

        isInsertHidden: function() {
          return !reportPartContent.isActiveForEdit;
        },

        getFormatDateByKey: function(key) {
          return get(key, getProperties('format'));
        },

        getReportId: function() {
          return fieldStore.reportId;
        }
      },

      onChangeValue: m.action(function(schemaData, changedKey, changedKeyPath, changedValue, changedOthersInfo) {
        var groupData = changedKeyPath && changedKeyPath.length ? get(changedKeyPath, schemaData) : schemaData;
        var fieldData = groupData[changedKey];

        fieldData && (fieldData.value = changedValue);

        var formId = reportPartContent.formState.id;
        var formProperties = {};
        var needUpdatePreview = false;

        // Handle action with value return
        if (changedKey === 'backgroundColor') {
          formProperties.backgroundColor = get('form.backgroundColor.value', schemaData);
        }

        if (changedKey === 'border') {
          formProperties = assign(formProperties, get('form.border.value', schemaData));
        }

        if (changedKey === 'field') {
          var fields = get('insert.field.value.selectedFields', schemaData);
          var selectedFields = isArray(fields) ? fields : fields.peek();

          reportPartContent.updateContainerField();

          selectedFields.forEach(function(data) {
            reportPart
              .insertFieldToContainer(reportPartContent.columns, data.field, data.querySource)
              .then(function(addedField) {
                var code = reportPartContent.getHTMLCodeField(addedField, data.querySource);
                reportPartContent.getEditorControl().insertContent(code);
              });
          });
        }
        if (changedKey === 'embedReport') {
          var value = schemaData.insert.embedReport.value;
          reportPartContent.insertEmbeddedReportHtml(value);
        }

        if (changedKey === 'wrapText') {
          formProperties.wrapText = schemaData.html.wrapText.value;
        }

        if (changedKey === 'highlightCode') {
          formProperties.highlightCode = schemaData.html.highlightCode.value;
        }

        if (changedKey === 'showInvisibleCharacters') {
          formProperties.mceVisualChars = get('view.showInvisibleCharacters.value', schemaData);
        }

        if (changedKey === 'visualAids') {
          formProperties.mceToggleVisualAid = get('view.visualAids.value', schemaData);
        }

        if (changedKey === 'dataRefreshInterval') {
          formProperties.dataRefreshInterval = get('view.dataRefreshInterval.value', schemaData);
        }

        if (changedKey === 'usePagination') {
          formProperties.usePagination = get('view.usePagination.value', schemaData);
          needUpdatePreview = true;
        }

        if (changedKey === 'pageBreakAfterEachEntry') {
          formProperties.pageBreakAfterEachEntry = get('printing.pageBreakAfterEachEntry.value', schemaData);
        }

        // Handle action by button group
        if (changedOthersInfo === 'pasteAsText') {
          formProperties.pasteAsText = !getOr(false, 'pasteAsText', getProperties());
        }

        if (changedOthersInfo === 'mceTemplateEmpty') {
          TinyMCEUtils.showNotify(formId, constants.MESSAGES.M15);
          return;
        }

        // Update model properties for form
        reportPart.setReportPartProperties(formProperties);
        // Update data when need update
        if (needUpdatePreview && AppGlobal.canShowPreview()) {
          fieldStore.needUpdatePreview();
        }
      })
    };
  };
})();



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartForm/Izenda.ReportPartPropsFormSchema.js